import { useLayoutEffect, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Provider as JotaiProvider } from 'jotai';
import { useAuth } from './infrastructure/auth0/useAuth';
import { queryClient } from './infrastructure/cache/client';
import { initializeUserApiClient } from './infrastructure/userApi/client';
import { initializeDataApiClient } from './infrastructure/dataApi/client';
import { Home } from './pages/Home';
import { LandingPage } from './pages/LandingPage';
import { Login } from './pages/Login';
import { Organization } from './pages/Organization';
import { SimViewer } from './pages/SimViewer';
import { Loading } from './components/Loading';
import { ErrorBoundary } from './components/ErrorBoundary';
import './App.less';

const App = (): ReactNode => {
  const { isLoading, isAuthenticated, currentUser, getAccessToken } = useAuth();

  // 子コンポーネントのEffectが実行される前に初期化を行う
  useLayoutEffect((): void => {
    initializeUserApiClient(getAccessToken);
    initializeDataApiClient(getAccessToken);
  }, [getAccessToken]);

  return (
    <QueryClientProvider client={queryClient}>
      <JotaiProvider>
        {isLoading ? (
          <Loading />
        ) : (
          <BrowserRouter>
            <ErrorBoundary>
              <Routes>
                {isAuthenticated ? (
                  <>
                    {currentUser?.isAdmin && (
                      <Route
                        path={'/organization'}
                        element={<Organization />}
                      />
                    )}
                    <Route
                      path={'/sim-viewer/:simdata_id'}
                      element={<SimViewer />}
                    />
                    <Route path={'/'} element={<Home />} />
                    <Route path={'*'} element={<Navigate to="/" replace />} />
                  </>
                ) : (
                  <>
                    <Route path={'/landing'} element={<LandingPage />} />
                    <Route path={'/login'} element={<Login />} />
                    <Route
                      path={'*'}
                      element={<Navigate to="/landing" replace />}
                    />
                  </>
                )}
              </Routes>
            </ErrorBoundary>
          </BrowserRouter>
        )}
      </JotaiProvider>
    </QueryClientProvider>
  );
};

export default App;
